import { makeAutoObservable } from 'mobx';
import dayjs from 'dayjs';
import { WorkoutCellViewModel } from 'components/WorkoutCell/WorkoutCellViewModel';
import { stores } from 'state/stores';
import { fireAndForget } from 'utils/async';
import { log } from 'utils/log';
import { runOnFocus } from 'utils/runOnFocus';

interface FeedWorkoutCell {
  type: 'workout-cell';
  vm: WorkoutCellViewModel;
}

interface LoadingCell {
  type: 'loading-cell';
  key: string;
}

interface MobileCTACell {
  type: 'mobile-cta';
}

type FeedCell = FeedWorkoutCell | LoadingCell | MobileCTACell;

export class FeedViewModel {
  isInitialFetch: boolean = true;
  isFetchingMore: boolean = false;
  private hasReachedBottomOfFeed: boolean = false;

  constructor() {
    makeAutoObservable(this);

    log('👷‍♂️ init FeedViewModel');
  }

  get showSuggestedUsers() {
    return !stores.serverStorage.disableSuggestedUsers;
  }

  private isFirstMount = true;
  onMounted = async () => {
    if (this.isFirstMount) {
      this.isFirstMount = false;

      if (process.browser) {
        this.refreshContent();
        runOnFocus({ promises: () => [this.refreshContent()], minimumElapsedMins: 20 });
      }
    }
  };

  private refreshContent = async () => {
    log('🔔 Fetching feed content');

    try {
      await fireAndForget([
        stores.workouts.refreshFeed(),
        stores.followingStatus.fetch(),
        stores.followCounts.fetch(),
        stores.workouts.fetchWorkoutCount(),
        stores.workouts.refreshUserWorkouts(stores.account.username), // Needed for Latest Activity section
      ]);
    } catch {
    } finally {
      this.isInitialFetch = false;
    }
  };

  handleScroll = (e: React.UIEvent<HTMLElement>) => {
    if (stores.workouts.feedWorkouts.length === 0) return;

    const scrollBottom = e.currentTarget.scrollTop + e.currentTarget.offsetHeight;

    const scrollHeight = e.currentTarget.scrollHeight;
    const fetchMoreThreshold = scrollHeight - 1000;

    if (scrollBottom > fetchMoreThreshold) {
      this.fetchMore();
    }
  };

  fetchMore = async () => {
    if (this.isFetchingMore || this.hasReachedBottomOfFeed) return;

    log('🛻 Fetching more workouts');

    const feedCount = stores.workouts.feedWorkouts.length;

    this.isFetchingMore = true;
    try {
      await stores.workouts.feedFetchMore();

      if (feedCount === stores.workouts.feedWorkouts.length) {
        this.hasReachedBottomOfFeed = true;
      }
    } catch {
    } finally {
      this.isFetchingMore = false;
    }
  };

  get cells(): FeedCell[] {
    if (this.isInitialFetch) {
      return [
        { type: 'loading-cell', key: '1' },
        { type: 'loading-cell', key: '2' },
      ];
    }

    const { feedWorkouts } = stores.workouts;

    const workoutCells = feedWorkouts.map(w => {
      return {
        type: 'workout-cell' as const,
        vm: new WorkoutCellViewModel(w),
      };
    });

    const dateOlderThanHevy = dayjs('2018-07-31T12:53:33.333Z');
    const lastWorkoutAt = dayjs(stores.account.last_workout_at);

    if (
      lastWorkoutAt.isValid() &&
      lastWorkoutAt.isAfter(dateOlderThanHevy) &&
      feedWorkouts.length > 0
    ) {
      return workoutCells;
    } else {
      return [{ type: 'mobile-cta' }, ...workoutCells];
    }
  }
}
